import {
  Media,
  MediaPickerComponent,
  MediaType,
  Severity,
} from '@gripp/shared-logic';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { array, mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { formStyles } from '../../styles/forms';
import { Colors } from '../../themes';
import { Button } from '../crop-circle/Button';
import { TypeIcon, TypeVariant } from '../crop-circle/Components/Type';
import { colors } from '../crop-circle/tokens';
import { ControlledTextInput } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { HorizontalMediaGallery, MediaGalleryComponent } from '../media';
import { MentionInput } from '../mentionInputField';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { LabelSelector } from './labelSelector';
import { QuickReminderField } from './quickReminderField';

export const NewIssueReportFormSchema = yupObject({
  message: string().required(),
  title: string().optional(),
  activityType: mixed<string>()
    .oneOf(['red', 'yellow', 'green', 'purple'])
    .required(),
  quickReminderDate: mixed().optional(),
  changeDisplayDate: mixed().optional(),
  adjustCreationDate: mixed().optional(),
  labels: array()
    .of(
      yupObject({
        id: string().required(),
        name: string().required(),
      })
    )
    .optional(),
});

export type NewIssueReportFormProps = {
  workspaceId?: string;
  mediaGallery?: MediaGalleryComponent;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  addMedia: (media: Media) => void;
  deleteMedia?: (mediaId: string) => void;
  setIsUploading?: (isUploading: boolean) => void;
  availableLabels?: Array<{ id: string; name: string }>;
  onCreateLabel?: (name: string) => Promise<{ id: string; name: string }>;
  watch: any;
  hideAdvancedSettings?: boolean;
  pickerMode?: 'picker' | 'camera';
  onSelectorEdit?: () => void;
};

export const NewIssueReportForm = (props: NewIssueReportFormProps) => {
  const { t } = useTranslation();
  const MediaPicker = props.mediaPicker;

  const isWeb = Platform.OS === 'web';

  const elementFocusCallBack = useAutoFocusRef();

  const renderFormContent = () => (
    <>
      <View style={{ ...formStyles.formItem, zIndex: 2 }}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.common.description')}
          </Text>
        </View>
        <Controller
          control={props.control}
          name="message"
          render={({ field: { onChange, value } }) => (
            <View style={styles.mentionInputContainer}>
              <MentionInput
                inputFieldText={value || ''}
                setInputFieldText={onChange}
                setIsMultiline={() => {}}
                inputHeight={100}
                setInputHeight={() => {}}
                send={() => {}}
                suggestionsPosition="bottom"
                handleEnter={false}
              />
            </View>
          )}
        />
      </View>
      <View style={formStyles.formItem}>
        <Text style={formStyles.formItemTitleCropCircle}>
          {t('issue.common.addPicture')}
        </Text>
        <HorizontalMediaGallery
          onMediaAdded={props.addMedia}
          onMediaDeleted={props.deleteMedia}
          mediaPicker={MediaPicker as MediaPickerComponent}
          onUploadingChanged={props.setIsUploading}
          workspaceId={props.workspaceId}
          acceptedMediaTypes={props.acceptedMediaTypes}
          canEditMedia={true}
          pickerMode={props.pickerMode}
        />
      </View>
      <View style={formStyles.formItem}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.new.type')}
          </Text>
        </View>
        <Controller
          control={props.control}
          name="activityType"
          render={({ field: { onChange, value } }) => (
            <ActivityType onChange={onChange} value={value} />
          )}
        />
      </View>
      <View style={formStyles.formItem}>
        <View>
          <Text style={formStyles.formItemTitleCropCircle}>
            {t('issue.new.title')}
          </Text>
        </View>
        <ControlledTextInput
          control={props.control}
          name="title"
          height={44}
          placeholder={t('issue.new.optionalPlaceholder')}
          disableInnerStyle={false}
          outlinedTextInput
          backgroundColor={Colors.neutral[200]}
          hideBorder
        />
      </View>
      {!props.hideAdvancedSettings && (
        <>
          {props.availableLabels && props.onCreateLabel && (
            <View style={formStyles.formItem}>
              <View>
                <Text style={formStyles.formItemTitleCropCircle}>
                  {t('issue.new.labels')}
                </Text>
              </View>
              <LabelSelector
                selectedLabels={props.watch('labels') || []}
                availableLabels={props.availableLabels}
                onLabelsChange={(labels) => {
                  props.setValue('labels', labels);
                }}
                onCreateLabel={props.onCreateLabel}
                onSelectorEdit={props.onSelectorEdit}
              />
            </View>
          )}
          <View style={formStyles.formItem}>
            <QuickReminderField
              control={props.control}
              setValue={props.setValue}
            />
          </View>
          <View style={formStyles.formItem}>
            <AdjustCreationDateField
              control={props.control}
              setValue={props.setValue}
            />
          </View>
        </>
      )}
    </>
  );

  return (
    <KeyboardSafeView style={styles.keyboardContainer}>
      {isWeb ? (
        <ScrollView
          contentContainerStyle={styles.formContainer}
          style={styles.webScrollView}
          showsVerticalScrollIndicator={true}
          scrollEnabled={true}
        >
          {renderFormContent()}
        </ScrollView>
      ) : (
        <ScrollView
          contentContainerStyle={styles.formContainer}
          style={{ borderRadius: 12 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
          scrollEnabled={true}
          automaticallyAdjustKeyboardInsets={true}
          keyboardDismissMode="interactive"
        >
          {renderFormContent()}
        </ScrollView>
      )}
    </KeyboardSafeView>
  );
};

const ActivityType: React.FC<{
  onChange: (...event: any[]) => void;
  value: Severity;
}> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const buttonOptions = [
    {
      label: t('activity.filter.urgent'),
      value: Severity.Red,
      iconCropCircleVariant: 'attention',
    },
    {
      label: t('activity.filter.pending'),
      value: Severity.Yellow,
      iconCropCircleVariant: 'blocker',
    },
    {
      label: t('activity.filter.solved'),
      value: Severity.Green,
      iconCropCircleVariant: 'solved',
    },
    {
      label: t('activity.filter.note'),
      value: Severity.Purple,
      iconCropCircleVariant: 'note',
    },
  ];

  return (
    <View style={styles.activityTypeCropCircleGrid}>
      {buttonOptions.map((option) => (
        <Button
          key={option.value}
          variant="rounded"
          onPress={() => onChange(option.value)}
          leftIcon={
            <TypeIcon variant={option.iconCropCircleVariant as TypeVariant} />
          }
          state={value === option.value ? 'active' : 'default'}
          style={{
            backgroundColor:
              value !== option.value
                ? colors.neutral[200]
                : colors.neutral[800],
          }}
        >
          {option.label}
        </Button>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  keyboardContainer: {
    flex: 1,
  },
  webScrollView: {
    flex: 1,
  },
  formContainer: {
    gap: 8,
    paddingBottom: 200, // Increased padding to ensure date picker doesn't hide the field
    paddingTop: 16, // Add top padding for better spacing
    flexGrow: 1, // Allow container to expand
  },
  subheaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  subheaderLine: {
    flex: 1,
    height: 1,
    backgroundColor: Colors.graySpacer,
  },
  subheaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginHorizontal: 10,
  },
  advancedSettingsTitle: {
    color: Colors.grayText,
    fontWeight: '400',
    lineHeight: 18,
    marginHorizontal: 10,
    fontSize: 14,
  },
  activityTypeCropCircleGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  mentionInputContainer: {
    borderRadius: 8,
    backgroundColor: Colors.neutral[200],
    padding: 8,
    minHeight: 100,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible', // Allow suggestions to show
  },
});
